import { Injectable, DOCUMENT, inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';


export const SITE_URL = 'https://michollo.com';
export const SITE_NAME = 'MiChollo';
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;
export const OG_DEFAULT_IMAGE = `${SITE_URL}/assets/images/og-default.jpg`;

export interface ProductMeta {
  price?: number;
  oldPrice?: number;
  currency?: string;
  /** Vigente según utils/chollo-vigente.ts; `undefined` = no se declara disponibilidad. */
  available?: boolean;
  /** Fecha de fin (AAAA-MM-DD), sólo si está vigente. */
  priceValidUntil?: string;
  brand?: string;
  breadcrumb?: Array<{ name: string, url: string }>;
  /**
   * `deals.updated_at` as ISO 8601: the ItemPage's `dateModified`. The API only bumps
   * it on content edits (visits, clicks and likes never touch it), the same value the
   * server publishes and the sitemap uses as <lastmod>. Absent = not declared.
   */
  modifiedAt?: string;
}

/** Seasonal landings with their own tabs in app-routing.module.ts (`blackfriday/nuevos/`…). */
const LANDINGS_CON_PESTANAS = ['blackfriday', '11-11', 'navidad', 'price-palace', 'aliexpressplaza', 'miravia'];
const PESTANAS_DE_LANDING = ['nuevos', 'onfire', 'populares', 'comentados', 'para_ti'];

/**
 * `/blackfriday/<x>/` that is not a router tab hangs from the wildcard `:id/:category/` and
 * renders an empty shell with the landing's metadata. The server serves it noindex,follow
 * with the canonical on the landing (social-meta.ts, `landingDeCascaron`); this is the same
 * rule for in-app navigation, where this service writes the tags. Returns the landing path,
 * or null for any real page (the landing, its tabs, stores, categories).
 */
export function landingDeCascaron(url: string): string | null {
  const ruta = String(url || '').replace(/^https?:\/\/[^/]+/i, '').split('?')[0].split('#')[0];
  const segmentos = ruta.split('/').filter(Boolean).map((s) => {
    try { return decodeURIComponent(s).toLowerCase(); } catch { return s.toLowerCase(); }
  });
  if (segmentos.length < 2 || LANDINGS_CON_PESTANAS.indexOf(segmentos[0]) === -1) return null;
  if (segmentos.length === 2 && PESTANAS_DE_LANDING.indexOf(segmentos[1]) !== -1) return null;
  return `/${segmentos[0]}/`;
}

/**
 * Title of a deal page: the editor's SEO title (`deals.metatitle`, plain text) when there
 * is one, the headline (`name`) otherwise. Same rule as the server (social-meta.ts,
 * `tituloSeoDeChollo`), so hydration writes the same <title>/og:title Googlebot saw. Only
 * the title changes: the <h1>, the breadcrumb and the slug keep `name`.
 */
export function tituloSeoDeChollo(chollo: { name?: string; metatitle?: string | null } | null | undefined): string {
  const seo = String(chollo?.metatitle ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return seo || (chollo?.name ?? '');
}

@Injectable({
  providedIn: 'root'
})
export class MetadataService {
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private document = inject(DOCUMENT);


  /** Tags que sólo existen en algunas páginas y hay que retirar al navegar. */
  private readonly optionalTags = [
    'article:published_time',
    'article:section',
    'product:price:amount',
    'product:price:currency',
    'og:price:amount',
    'og:price:currency',
    'product:availability',
    'product:brand',
  ];

  constructor() {
              this.createLinkForCanonicalURL();
            }

  setMeta(title, description = null, image = null, createdAt = null, category = null, product: ProductMeta = null) {
    const canonical = this.canonicalDeLaPagina();
    // Misma marca que escribe el servidor (social-meta.ts, `renderSocialHead`). Aquí ponía
    // «» Michollo.com», así que el título cambiaba de «» MiChollo» a «» Michollo.com» al
    // arrancar Angular, y Google veía los dos.
    const fullTitle = /michollo/i.test(title || '') ? `${title}` : `${title} » ${SITE_NAME}`;
    const socialImage = this.toSocialImage(image);
    const isProduct = !!product;

    this.titleService.setTitle(fullTitle);

    // Una página buena tras un 404 tiene que borrar el noindex anterior: la app es
    // una SPA y el <head> sobrevive a la navegación.
    this.clearNoIndex();
    // Shell of a seasonal landing (/blackfriday/aliexpress/): the same noindex,follow the
    // server writes, so an in-app navigation does not turn it back into an indexable page.
    if (landingDeCascaron(this.currentUrl())) this.metaService.updateTag({ name: 'robots', content: 'noindex, follow' });

    description = this.truncate(this.stripTags(description), 200) ||
      'Descubre los mejores chollos, ofertas y códigos descuento seleccionados cada día por la comunidad de MiChollo.';

    this.metaService.updateTag({ name: 'description', content: description });

    // Twitter Cards: el estándar usa name=, no property=.
    // summary_large_image muestra la foto a ancho completo; summary la deja en miniatura.
    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: fullTitle });
    this.metaService.updateTag({ name: 'twitter:description', content: description });
    this.metaService.updateTag({ name: 'twitter:image', content: socialImage });
    this.metaService.updateTag({ name: 'twitter:image:alt', content: title || SITE_NAME });
    this.metaService.updateTag({ name: 'twitter:site', content: '@michollo' });

    // Open Graph
    this.metaService.updateTag({ property: 'og:site_name', content: SITE_NAME });
    this.metaService.updateTag({ property: 'og:locale', content: 'es_ES' });
    // og:url debe ser la URL canónica absoluta: Facebook la usa como identidad del
    // objeto compartido. Un valor fijo colapsa todas las páginas en una sola entidad.
    this.metaService.updateTag({ property: 'og:url', content: canonical });
    this.metaService.updateTag({ property: 'og:type', content: isProduct ? 'product' : 'website' });
    this.metaService.updateTag({ property: 'og:title', content: fullTitle });
    this.metaService.updateTag({ property: 'og:description', content: description });

    this.metaService.updateTag({ property: 'og:image', content: socialImage });
    this.metaService.updateTag({ property: 'og:image:secure_url', content: socialImage });
    // Sólo se declaran las dimensiones de la imagen por defecto, que son conocidas.
    // El CDN preserva la relación de aspecto al redimensionar, así que la altura de
    // la foto de un chollo es variable: declararla mal hace que Facebook la recorte
    // mal o la descarte, que es peor que dejar que la mida él.
    if (socialImage === OG_DEFAULT_IMAGE) {
      this.metaService.updateTag({ property: 'og:image:width', content: `${OG_IMAGE_WIDTH}` });
      this.metaService.updateTag({ property: 'og:image:height', content: `${OG_IMAGE_HEIGHT}` });
    } else {
      this.metaService.removeTag(`property='og:image:width'`);
      this.metaService.removeTag(`property='og:image:height'`);
    }
    this.metaService.updateTag({ property: 'og:image:alt', content: title || SITE_NAME });
    this.metaService.updateTag({
      property: 'og:image:type',
      content: /\.png(\?|#|$)/i.test(socialImage) ? 'image/png' : 'image/jpeg',
    });

    // Las tags opcionales se retiran siempre antes de volver a ponerlas, para que
    // no queden residuos de la página anterior al navegar dentro de la SPA.
    this.optionalTags.forEach(name => this.metaService.removeTag(`property='${name}'`));

    if (createdAt) {
      // El namespace correcto es "article:", sin el prefijo "og:".
      this.metaService.updateTag({ property: 'article:published_time', content: createdAt });
    }

    if (category) {
      this.metaService.updateTag({ property: 'article:section', content: category });
    }

    if (isProduct) {
      const currency = product.currency || 'EUR';
      if (typeof product.price === 'number' && isFinite(product.price) && product.price >= 0) {
        const amount = product.price.toFixed(2);
        this.metaService.updateTag({ property: 'product:price:amount', content: amount });
        this.metaService.updateTag({ property: 'product:price:currency', content: currency });
        this.metaService.updateTag({ property: 'og:price:amount', content: amount });
        this.metaService.updateTag({ property: 'og:price:currency', content: currency });
      }
      // product:availability con la regla de «vigente», igual que el servidor (13/10/2026).
      // Está en optionalTags para que se limpie al navegar.
      if (typeof product.available === 'boolean') {
        this.metaService.updateTag({ property: 'product:availability', content: product.available ? 'in stock' : 'out of stock' });
      }
      if (product.brand) {
        // Nada de product:brand: la marca ya va en el JSON-LD como schema.org/Brand y
      // Google mezcla ambas fuentes en la ficha de comerciante, dando "brand duplicado".
      }
    }

    this.createLinkForCanonicalURL();
    this.setStructuredData({ title: fullTitle, description, image: socialImage, url: canonical, product, category, publishedAt: createdAt });
  }

  /**
   * Marca la vista actual como no indexable sin tocar la canónica.
   *
   * La usa la ruta de "no encontrado", que se renderiza sin cambiar la URL: la
   * página no existe, pero la URL que el usuario pidió es la que Google debe ver
   * marcada, no una canónica hacia /pagina-no-encontrada/.
   */
  setNotFound() {
    this.metaService.updateTag({ name: 'robots', content: 'noindex, follow' });
  }

  private clearNoIndex() {
    // Si el `noindex` lo escribió el SERVIDOR para esta misma URL, se queda. El servidor
    // sabe cosas que este servicio no: que la etiqueta no tiene chollos (lote C64) o que
    // es la página 2+ de un listado. Antes, al arrancar Angular, `setMeta` lo borraba y
    // la página volvía a declararse `index, follow`.
    if (this.noindexServido && this.urlSinHash() === this.urlServida) return;

    const robots = this.metaService.getTag('name="robots"');
    if (robots && (robots.getAttribute('content') || '').indexOf('noindex') !== -1) {
      this.metaService.updateTag({ name: 'robots', content: 'index, follow, max-image-preview:large' });
    }
  }

  createLinkForCanonicalURL() {
    const href = this.canonicalDeLaPagina();
    let canonical: any = this.document.querySelector('link[rel="canonical"]');

    // En el DOM de servidor (Domino, platform-server) querySelector con selector de
    // atributo NO matchea el <link rel="canonical"> del template → devuelve undefined.
    // Fallback a mano para no crear un canonical DUPLICADO en el HTML SSR.
    if (!canonical && this.document.getElementsByTagName) {
      const links = this.document.getElementsByTagName('link');
      for (let i = 0; i < links.length; i++) {
        if (links[i].getAttribute && links[i].getAttribute('rel') === 'canonical') { canonical = links[i]; break; }
      }
    }

    if (!canonical) {
      canonical = this.document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      this.document.head.appendChild(canonical);
    }

    // Mientras sigamos en la página que renderizó el servidor, su canonical manda. En
    // las sub-vistas de una etiqueta (/hogar/nuevos/, /amazon-es/hogar/) apunta a la
    // etiqueta madre a propósito, porque enseñan la misma colección ordenada de otra
    // forma; sobrescribirlo con la URL actual devolvía cada sub-vista a competir como
    // página propia.
    //
    // La condición es la RUTA, no una marca de un solo uso: este método se llama varias
    // veces por página (constructor + cada setMeta), así que consumir la marca en la
    // primera llamada dejaba que la segunda pisara el valor igual.
    if (canonical.getAttribute('data-ssr') === '1') {
      if (this.rutaDe(href) === this.rutaServida) return;
      canonical.removeAttribute('data-ssr');
    }

    canonical.setAttribute('href', href);
  }

  /** URL canónica absoluta de la página actual, sin query ni hash. */
  /** Ruta de una URL, sin origen ni barra final, para comparar páginas. */
  private rutaDe(url: string): string {
    return String(url || '').split('?')[0].split('#')[0].replace(/^https?:\/\/[^/]+/i, '').replace(/\/+$/, '') || '/';
  }

  /**
   * Ruta que renderizó el servidor. Mientras no se navegue a otra, sus metadatos
   * (canonical y JSON-LD) mandan sobre los que compone este servicio.
   */
  private readonly rutaServida = this.rutaDe(this.document.URL || '/');

  private urlSinHash(): string {
    return String(this.document.URL || '').split('#')[0];
  }

  /** URL exacta (con query: `?page=2` es otra página) que renderizó el servidor. */
  private readonly urlServida = this.urlSinHash();

  /** El HTML del servidor traía `<meta name="robots" content="noindex…">`. */
  private readonly noindexServido = (() => {
    const robots = this.document.querySelector('meta[name="robots"]');
    return !!robots && (robots.getAttribute('content') || '').indexOf('noindex') !== -1;
  })();

  /** Canonical of the current page: itself, or its landing for a seasonal shell. */
  private canonicalDeLaPagina(): string {
    const url = this.currentUrl();
    const landing = landingDeCascaron(url);
    return landing ? `${SITE_URL}${landing}` : url;
  }

  private currentUrl(): string {
    let url = (this.document.URL || `${SITE_URL}/`).split('?')[0].split('#')[0];
    // En SSR (Domino) document.URL es https://localhost/<ruta>: salían canonical y
    // og:url apuntando a localhost. Se sustituye el origen por el del sitio.
    url = url.replace(/^https?:\/\/localhost(:\d+)?/i, SITE_URL);
    return url.replace(/^http:\/\//i, 'https://');
  }

  /**
   * Devuelve una imagen que los crawlers sociales sepan renderizar.
   *
   * Descarta SVG (no lo soporta ninguna red) y fuerza el ancho de 1200px en el CDN
   * propio, que redimensiona por segmento de ruta: las miniaturas de 60px que
   * devuelve la API quedan por debajo del mínimo de 200x200 que exige Facebook.
   */
  private toSocialImage(imageUrl: any): string {
    const raw = typeof imageUrl === 'string' ? imageUrl.trim() : '';
    if (!raw || raw === 'null' || raw === 'undefined') return OG_DEFAULT_IMAGE;
    if (/\.svgz?(\?|#|$)/i.test(raw)) return OG_DEFAULT_IMAGE;

    let url = raw.startsWith('//') ? `https:${raw}` : raw;
    if (!/^https?:\/\//i.test(url)) url = `${SITE_URL}/${url.replace(/^\/+/, '')}`;
    url = url.replace(/^http:\/\//i, 'https://');

    if (/\/r\/\d+\//.test(url)) return url.replace(/\/r\/\d+\//, `/r/${OG_IMAGE_WIDTH}/`);
    if (/img\.michollo\.com\//i.test(url)) {
      return url.replace(/(img\.michollo\.com)\/(?!r\/)/i, `$1/r/${OG_IMAGE_WIDTH}/`);
    }
    return url;
  }

  private stripTags(value: any): string {
    return String(value === null || value === undefined ? '' : value)
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;|&apos;/gi, `'`)
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Trunca por palabra completa para no partir entidades ni caracteres multibyte. */
  private truncate(value: string, max: number): string {
    const text = (value || '').trim();
    if (Array.from(text).length <= max) return text;
    const cut = Array.from(text).slice(0, max).join('');
    const lastSpace = cut.lastIndexOf(' ');
    const base = lastSpace > max * 0.5 ? cut.slice(0, lastSpace) : cut;
    return base.replace(/[\s,;.:·\-–—]+$/, '') + '…';
  }

  /** Inserta o reemplaza el bloque JSON-LD de la página. */
  private setStructuredData({ title, description, image, url, product, category, publishedAt = null }) {
    try {
      const graph: any[] = [
        {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: SITE_NAME,
          url: `${SITE_URL}/`,
          logo: `${SITE_URL}/assets/images/og-default.png`,
          sameAs: [
            'https://www.facebook.com/michollocom/',
            'https://twitter.com/michollo',
            'https://www.instagram.com/michollocom/',
            'https://www.youtube.com/c/MiChollo',
            'https://www.linkedin.com/company/michollo',
          ],
        },
        {
          '@type': 'WebSite',
          '@id': `${SITE_URL}/#website`,
          url: `${SITE_URL}/`,
          name: SITE_NAME,
          inLanguage: 'es-ES',
          publisher: { '@id': `${SITE_URL}/#organization` },
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${SITE_URL}/results/?search={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
          },
        },
      ];

      if (product) {
        const moneda = product.currency || 'EUR';
        const dosDecimales = (n: number) => Math.round(n * 100) / 100;
        const esPrecio = (n: unknown): n is number => typeof n === 'number' && isFinite(n) && n >= 0;

        const offer: any = {
          '@type': 'Offer',
          url,
          priceCurrency: moneda,
        };
        if (esPrecio(product.price)) offer.price = dosDecimales(product.price);
        if (product.brand) offer.seller = { '@type': 'Organization', name: product.brand };

        // EL PRECIO ANTERIOR, que es la mitad de lo que vende un chollo. Google lo lee
        // como `StrikethroughPrice` dentro de `priceSpecification` y con él puede pintar el
        // tachado y el porcentaje en el propio resultado de búsqueda. `oldPrice` ya
        // llegaba hasta aquí desde la ficha y no se usaba para nada.
        // Sólo si es MAYOR que el precio actual: un "antes" más barato que el "ahora"
        // no es un descuento, es un dato malo, y declararlo sería mentir en el markup.
        if (esPrecio(offer.price) && esPrecio(product.oldPrice) && product.oldPrice > offer.price) {
          offer.priceSpecification = {
            '@type': 'UnitPriceSpecification',
            priceType: 'https://schema.org/StrikethroughPrice',
            priceCurrency: moneda,
            price: dosDecimales(product.oldPrice),
          };
        }

        // `availability` y `priceValidUntil` con la regla de «vigente» (13/10/2026), igual
        // que el servidor; el porqué, con números, está en social-meta.ts. `available`
        // llega ya calculado con esVigente desde product-category.page.ts.
        if (typeof product.available === 'boolean') {
          offer.availability = product.available ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';
        }
        if (product.priceValidUntil) offer.priceValidUntil = product.priceValidUntil;

        const node: any = {
          '@type': 'Product',
          '@id': `${url}#product`,
          name: title,
          description,
          image: [image],
          url,
          // Sin precio no se declara oferta: un Offer sin price es un error, no un aviso.
          offers: typeof offer.price === 'number' ? offer : undefined,
        };
        // `product.brand` es la tienda, no el fabricante: no se declara como marca.
        if (category) node.category = category;
        graph.push(node);

        // The page node and the breadcrumb: same shape as the server's (social-render.ts).
        graph.push(itemPageNode({ url, title, description, image, publishedAt, product }));
        const migas = breadcrumbNode(url, product.breadcrumb);
        if (migas) graph.push(migas);
      }

      // Reutiliza el bloque que ya inyectó el servidor (mismo id): si se creara uno
      // nuevo quedarían dos nodos Product con idéntico @id y Search Console lo
      // reporta como "el campo brand está duplicado".
      let script: any = this.document.getElementById('michollo-jsonld');

      // Primera carga: si el bloque lo escribió el servidor Y describe ESTA misma URL,
      // se deja como está. El del servidor es más completo (CollectionPage/ItemPage,
      // ItemList del listado, migas) porque allí se conocen las ofertas servidas.
      // Se quita la marca para que la siguiente navegación dentro de la app sí lo
      // reescriba: a partir de ahí el servidor ya no sabe en qué página estamos.
      if (script && script.getAttribute('data-ssr') === '1') {
        if (this.rutaDe(url) === this.rutaServida) return;
        script.removeAttribute('data-ssr');
      }

      if (!script) {
        script = this.document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('id', 'michollo-jsonld');
        this.document.head.appendChild(script);
      }

      // Evita que un "</script>" dentro de los datos cierre el bloque antes de tiempo.
      script.textContent = JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })
        .replace(/</g, '\\u003c');
    } catch (err) {
      console.error('No se pudo escribir el JSON-LD', err);
    }
  }

}

interface ItemPageInput {
  url: string;
  title: string;
  description: string;
  image: string;
  publishedAt: string | null;
  product: { breadcrumb?: { name: string; url: string }[]; modifiedAt?: string };
}

/**
 * The ItemPage node of a deal: hangs the URL from the WebSite, points at its main entity and
 * its breadcrumb, and carries the CreativeWork fields (author, datePublished, dateModified)
 * that a Product cannot. The author is the site itself: the uploading user is not a reliable
 * byline and the Organization node is already in the graph. Same shape as social-render.ts.
 */
function itemPageNode({ url, title, description, image, publishedAt, product }: ItemPageInput): Record<string, unknown> {
  const page: Record<string, unknown> = {
    '@type': 'ItemPage',
    '@id': `${url}#webpage`,
    url,
    name: title,
    description,
    inLanguage: 'es-ES',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    mainEntity: { '@id': `${url}#product` },
    author: { '@id': `${SITE_URL}/#organization` },
  };
  if (product.breadcrumb && product.breadcrumb.length) page['breadcrumb'] = { '@id': `${url}#breadcrumb` };
  if (publishedAt) page['datePublished'] = publishedAt;
  if (product.modifiedAt) page['dateModified'] = product.modifiedAt;
  if (image) page['primaryImageOfPage'] = image;
  return page;
}

function breadcrumbNode(url: string, crumbs?: { name: string; url: string }[]): Record<string, unknown> | null {
  if (!crumbs || !crumbs.length) return null;
  return {
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
}
